import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  AddMemberInRoomBody,
  CreateChatRoomBody,
  MessageConfirmationBody,
  PinRoomBody,
  RemoveMemberInRoomBody,
  SetRoomAdminBody,
  TransferOwnerBody,
} from "../../types/chat";
import { chatApi } from "../../api/chat";
import { message } from "antd";

export const useCreateChatRoom = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body: CreateChatRoomBody) => chatApi.createChatRoom(body),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["get-chats"] });
    },
  });
};

export const useAddMemberInRoom = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body: AddMemberInRoomBody) => chatApi.addMemberInRoom(body),
    onSuccess: async (data, variables) => {
      await queryClient.invalidateQueries({ queryKey: ["get-chats"] });
      await queryClient.invalidateQueries({ queryKey: ["join-a-chat-room"] });
      // Invalidate messages để hiển thị tin nhắn hệ thống "thêm thành viên"
      await queryClient.invalidateQueries({ queryKey: ["infinite-messages"] });
    },
  });
};

export const useRemoveMemberInRoom = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body: RemoveMemberInRoomBody) => chatApi.removeMemberInRoom(body),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["get-chats"] });
      await queryClient.invalidateQueries({ queryKey: ["join-a-chat-room"] });
      // Invalidate messages để hiển thị tin nhắn hệ thống "xóa thành viên"
      await queryClient.invalidateQueries({ queryKey: ["infinite-messages"] });
    },
  });
};

export const useSetRoomAdmin = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body: SetRoomAdminBody) => chatApi.setRoomAdmin(body),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["get-chats"] });
      await queryClient.invalidateQueries({ queryKey: ["join-a-chat-room"] });
      await queryClient.invalidateQueries({ queryKey: ["check-room-admin"] });
      await queryClient.invalidateQueries({ queryKey: ["check-list-user-in-room-admin"] });
    },
  });
};

export const usePinRoom = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body: PinRoomBody) => chatApi.pinRoom(body),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["get-chats"] });
      await queryClient.invalidateQueries({ queryKey: ["join-a-chat-room"] });
    },
  });
};

export const useMuteRoom = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (roomId: string) => chatApi.muteRoom(roomId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["get-chats"] });
      await queryClient.invalidateQueries({ queryKey: ["join-a-chat-room"] });
    },
  });
};

export const useCloseRoom = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (roomId: string) => chatApi.closeRoom(roomId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["get-chats"] });
      await queryClient.invalidateQueries({ queryKey: ["join-a-chat-room"] });
    },
    onError: () => {
      message.error("Không thể giải tán nhóm này.");
    },
  });
};

export const useTransferOwner = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body: TransferOwnerBody) => chatApi.transferOwner(body),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["get-chats"] });
      await queryClient.invalidateQueries({ queryKey: ["join-a-chat-room"] });
      await queryClient.invalidateQueries({ queryKey: ["check-room-admin"] });
      await queryClient.invalidateQueries({ queryKey: ["check-list-user-in-room-admin"] });
    },
  });
};

export const useConfirmMessage = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body: MessageConfirmationBody) => chatApi.confirmMessage(body),
    onSuccess: async () => {
      // Invalidate confirmation data to refresh button visibility
      await queryClient.invalidateQueries({ queryKey: ["get-member-confirmation"] });
      await queryClient.invalidateQueries({ queryKey: ["get-confirmed-message-ids"] });
      await queryClient.invalidateQueries({ queryKey: ["get-chats"] });
      await queryClient.invalidateQueries({ queryKey: ["join-a-chat-room"] });
    },
  });
};
